import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';

export const DEFAULT_TO_VALUE = 1_000_000;

export const NUMBER_RANGE_UPPER_MAX = 100_000_000;

interface UseNumberRangeFilterOptions {
  defaultFrom?: number;
  defaultTo?: number;
  unit?: string;
  placeholder?: string;
  onChange?: (from: string, to: string) => void;
  onClear?: () => void;
}

export function useNumberRangeFilter(
  value: string[],
  min: number,
  max: number,
  options: UseNumberRangeFilterOptions = {}
) {
  const [open, setOpen] = useState(false);

  const {
    defaultFrom = min,
    defaultTo = max,
    unit,
    placeholder,
    onChange,
    onClear,
  } = options;

  const isEmptyFilter = value.length === 1 && value[0] === EMPTY_FILTER_VALUE;

  const fromValue = isEmptyFilter || !value[0] ? defaultFrom : Number(value[0]);
  const toValue = isEmptyFilter || !value[1] ? defaultTo : Number(value[1]);

  const [from, setFrom] = useState<number | ''>(fromValue);
  const [to, setTo] = useState<number | ''>(toValue);

  const clamp = useCallback(
    (n: number) => Math.min(max, Math.max(min, n)),
    [min, max]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const appliedFrom = value[0] ? Number(value[0]) : defaultFrom;
        const appliedTo = value[1] ? Number(value[1]) : defaultTo;
        setFrom(clamp(appliedFrom));
        setTo(clamp(appliedTo));
      }

      setOpen(nextOpen);
    },
    [clamp, defaultFrom, defaultTo, value]
  );

  const handleSliderChange = useCallback(
    (range: number[]) => {
      const [a, b] = range;
      const low = clamp(Math.min(a, b));
      const high = clamp(Math.max(a, b));
      setFrom(low);
      setTo(high);
    },
    [clamp]
  );

  const handleFromInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return setFrom('');

      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const v = clamp(num);
      setFrom(v);
      if (to !== '' && v > to) setTo(v);
    },
    [clamp, to]
  );

  const handleToInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return setTo('');

      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const v = clamp(num);
      setTo(v);
      if (from !== '' && v < from) setFrom(v);
    },
    [clamp, from]
  );

  const handleClear = useCallback(() => {
    setFrom(defaultFrom);
    setTo(defaultTo);
    onClear?.();
  }, [defaultFrom, defaultTo, onClear]);

  const formatWithUnit = useCallback(
    (value: number) => (unit ? `${value} ${unit}` : String(value)),
    [unit]
  );

  const handleConfirm = useCallback(() => {
    const fromNum = from === '' ? defaultFrom : clamp(from);
    const toNum = to === '' ? defaultTo : clamp(to);
    onChange?.(String(fromNum), String(toNum));
    handleOpenChange(false);
  }, [from, to, defaultFrom, defaultTo, clamp, onChange, handleOpenChange]);

  const hasAppliedRange = value.length >= 2 && value[0] !== EMPTY_FILTER_VALUE;
  const isDefaultRange =
    (from === '' || from === defaultFrom) && (to === '' || to === defaultTo);
  const buttonLabel = useMemo(
    () =>
      hasAppliedRange
        ? `${formatWithUnit(fromValue)} - ${formatWithUnit(toValue)}`
        : (placeholder ?? null),
    [hasAppliedRange, fromValue, toValue, placeholder, formatWithUnit]
  );
  const sliderValue = [
    from === '' ? defaultFrom : from,
    to === '' ? defaultTo : to,
  ];

  return {
    from,
    to,
    fromValue,
    toValue,
    hasAppliedRange,
    isDefaultRange,
    buttonLabel,
    sliderValue,
    open,
    handleSliderChange,
    handleFromInputChange,
    handleToInputChange,
    handleClear,
    handleConfirm,
    formatWithUnit,
    setOpen: handleOpenChange,
  };
}
